import {AfterViewInit, Component, ElementRef, Input, OnChanges, TemplateRef, ViewChild} from '@angular/core';
import {View} from "src/app/common/view";
import {Handler} from "src/app/common/handler";
import {AlertEntity} from "../../../../handlers/issues-standalone-handler";
import {
    Alert,
    AlertStatus,
    AlertType,
    MeterType,
    MuteAlert,
    ResolveAlert,
    UnmuteAlert,
    UserProfile
} from "@flowmaps-com/flowmaps-typescriptmodels";
import {RefdataUtils} from "../../../refdata/refdata-utils";
import {AppContext} from "../../../../app-context";
import moment from "moment";
import {openConfirmationModalWithCallback} from "../../../../app-utils";
import {
    ModalConfirmAutofocus,
    ModalConfirmAutofocusData
} from "../../../../common/modal-confirm/modal-confirm.component";
import {ChartOptionType, Dashboard} from "../../../dashboard/dashboard.types";
import {DashboardContext} from "../../../dashboard/dashboard.context";
import {AlertNoteDetailsComponent} from "../../alert-note-details/alert-note-details.component";
import {AlertUserDetailsComponent} from "../../alert-user-details/alert-user-details.component";
import Tooltip from 'bootstrap/js/dist/tooltip';
import {defaultModalOptions} from "../../../../common/modal/modal";
import {cloneDeep} from "lodash";
import {AppCommonUtils} from "../../../../common/app-common-utils";
import {localTimeFormat} from "../../../../common/utils";
import {INCIDENT_OPTIONS} from "../../../charts/base-measurement-chart";

@Component({
    selector: 'app-alert-overview-item',
    templateUrl: './alert-overview-item.component.html',
    styleUrls: ['./alert-overview-item.component.scss']
})
@Handler()
export class AlertOverviewItemComponent extends View implements AfterViewInit, OnChanges {
    appContext = AppContext;
    refDataUtils = RefdataUtils;
    @Input() alertEntity: AlertEntity;
    @Input() selecting: boolean;
    user: UserProfile;
    muteDate: string;
    tooltipInstance!: Tooltip;
    @ViewChild('tooltipRef', { static: false }) tooltipRef!: ElementRef;
    @ViewChild("muteAlertTemplate") muteAlertTemplate: TemplateRef<any>;

    ngAfterViewInit() {
        this.updateTooltip()
    }

    ngOnChanges() {
        this.updateTooltip();
        this.subscribeTo("getUserById", this.alertEntity.alert.assigneeId).subscribe(u => {
            this.user = u;
        })
    }

    updateTooltip() {
        if (this.tooltipRef?.nativeElement && this.alert.note?.length) {
            this.tooltipInstance = new Tooltip(this.tooltipRef.nativeElement, {
                html: true,
                title: `<div class="text-start notranslate" ">${this.alert.note.replace(/\n/g, '<br>')}</div>`,
                trigger: 'hover focus',
                placement: 'top',
                customClass: 'tooltip-wide'
            });
        }
    }

    hasNote(alert: Alert): boolean {
        return !!alert.note?.trim();
    }

    openConnectionGraph = () => {
        const timestamp = this.alert.timestamp;
        const start = moment(timestamp).startOf('year').format(localTimeFormat);
        const end = moment(timestamp).add(1, 'year').startOf('year').format(localTimeFormat);
        const dashboard = DashboardContext.connectionDashboardWithTimeRange(
            this.alert.details.entityIds[1], { start, end, label: `${moment(timestamp).year()}` }
        );
        this.updateChartOptions(dashboard)
        AppCommonUtils.navigateToUrl('/dashboard/' + DashboardContext.dashboardToBase64(dashboard));
    }

    get alert(): Alert {
        return this.alertEntity.alert;
    }

    get assigneeName(): string {
        this.subscribeTo("getUserById", this.alertEntity.alert.assigneeId).subscribe(u => {
            this.user = u;
        })
        return RefdataUtils.userFormatter(this.user)
    }

    private updateChartOptions = (dashboard: Dashboard) => {
        const ct = this.alert.connectionType as unknown as ChartOptionType | undefined;
        if (!ct || !(ct in dashboard.info.chartOptions)) return;
        dashboard.info.chartOptions[ct].showIncidents = true;
        dashboard.info.chartOptions[ct].selectedIncidents = [INCIDENT_OPTIONS.find(opt => opt.value === this.alert.details.type)];
        if (this.alert.meterType === MeterType.GROSS_PRODUCTION) {
            dashboard.info.chartOptions[ct].showGrossProduction = true;
        }
    };

    tomorrow = (): string => moment().startOf("day").add(1, "day").toISOString();
    formatTimestamp = (date: string) => moment(date).format(AppContext.displayFormat);

    isResolved = (): boolean => AlertStatus.RESOLVED == this.alert.alertStatus;
    isMuted = (): boolean => AlertStatus.MUTED == this.alert.alertStatus;

    toggleSelectedItem = (value: boolean) => this.sendCommandAndForget("selectionChange", {
        entity: this.alertEntity,
        selected: value
    });

    getAlertStatusConfig = (): AlertStatusConfig => {
        switch (this.alert.alertStatus) {
            case AlertStatus.RESOLVED:
                return {
                    colorClass: "success",
                    icon: "bi-check-circle"
                };
            case AlertStatus.MUTED:
                return {
                    colorClass: "muted",
                    icon: "bi-volume-mute"
                };
            default:
                return {
                    colorClass: this.badgeColorForAlertType(),
                    icon: "bi-record-circle"
                };
        }
    }

    badgeColorForAlertType = () => {
        if (this.alert.alertStatus === AlertStatus.RESOLVED) {
            return "light";
        }
        switch (this.alert.details.type) {
            case AlertType.Disconnected:
                return "danger";
            case AlertType.MissingData:
                return "warning";
            case AlertType.ContractedPowerExceeded:
            case AlertType.ContractedFeedInPowerExceeded:
                return "danger-dark";
            case AlertType.Peak:
                return "secondary";
            default:
                return "info";
        }
    }

    getLocationDashboardLink = (): string => {
        const locationDashboard: Dashboard = DashboardContext.defaultDashboard();
        locationDashboard.info.sources.locationIds = [this.alertEntity.entity.location.locationId];
        this.updateChartOptions(locationDashboard)
        return `/dashboard/${DashboardContext.dashboardToBase64(locationDashboard)}`;
    }

    openResolveAlertModal = () => openConfirmationModalWithCallback((confirmed) => {
        if (confirmed) {
            this.sendCommand("com.flowmaps.api.monitoring.alerting.ResolveAlert", <ResolveAlert>{
                alertId: this.alert.alertId
            }, () => this.alertEntity.alert.alertStatus = AlertStatus.RESOLVED);
        }
    }, ModalConfirmAutofocus, <ModalConfirmAutofocusData>{
        type: "info",
        title: "Resolve alert",
        message: "Are you sure you want to resolve this alert?",
        confirmText: "Resolve alert",
        cancelText: "Cancel"
    }, 'static')

    openMuteAlertModal = () => {
        this.muteDate = this.alert.muteDeadline;
        openConfirmationModalWithCallback((confirmed) => {
            if (confirmed) {
                this.sendCommand("com.flowmaps.api.monitoring.alerting.MuteAlert", <MuteAlert>{
                    alertId: this.alert.alertId,
                    deadline: this.muteDate
                }, () => {
                    this.alertEntity.alert.alertStatus = AlertStatus.MUTED;
                    this.alertEntity.alert.muteDeadline = this.muteDate;
                    this.muteDate = null;
                });
            }
        }, ModalConfirmAutofocus, <ModalConfirmAutofocusData>{
            type: "info",
            title: "Mute alert",
            body: this.muteAlertTemplate,
            confirmText: "Mute alert",
            cancelText: "Cancel"
        }, 'static');
    }

    openDeleteNoteModal() {
        openConfirmationModalWithCallback((confirmed) => {
            if (confirmed) {

                this.sendCommand("com.flowmaps.api.monitoring.alerting.UpsertAlertNote", {
                    alertId: this.alert.alertId,
                    note: null
                }, () => {
                    this.alert.note = null;
                });
            }
        }, ModalConfirmAutofocus, <ModalConfirmAutofocusData>{
            type: "danger",
            title: "Delete note",
            message: "Are you sure you want to delete this note?",
            confirmText: "Delete",
            cancelText: "Cancel"
        }, 'static');
    }

    openRemoveUserModal() {
        openConfirmationModalWithCallback((confirmed) => {
            if (confirmed) {
                this.sendCommand('com.flowmaps.api.monitoring.alerting.AssignUserToAlert', {
                    alertId: this.alert.alertId,
                    assigneeId: null
                }, () => {
                    this.alert.assigneeId = null;
                });
            }
        }, ModalConfirmAutofocus, <ModalConfirmAutofocusData>{
            type: "danger",
            title: "Remove user",
            message: "Are you sure you want to remove this user from this alert?",
            confirmText: "Delete",
            cancelText: "Cancel"
        }, 'static');
    }

    openUnmuteAlertModal = () => this.sendCommand("com.flowmaps.api.monitoring.alerting.UnmuteAlert", <UnmuteAlert>{
        alertId: this.alert.alertId
    }, () => {
        this.alertEntity.alert.alertStatus = AlertStatus.OPEN;
        this.alertEntity.alert.muteDeadline = null;
    })

    updateNote() {
        this.openModal(AlertNoteDetailsComponent, cloneDeep({alert: this.alertEntity.alert, note: this.alertEntity.alert.note}));
    }

    assignUser() {
        this.openModal(
            AlertUserDetailsComponent,
            cloneDeep({ alert: this.alertEntity.alert, user: this.user }),
            { ...defaultModalOptions, cssClass: 'modal-lg modal-dialog-centered' }
        );
    }

    openReopenAlertModal = () => openConfirmationModalWithCallback((confirmed) => {
        if (confirmed) {
            this.sendCommand('com.flowmaps.api.monitoring.alerting.ReopenAlert', {
                alertId: this.alert.alertId
            });
        }
    }, ModalConfirmAutofocus, <ModalConfirmAutofocusData>{
        type: "danger",
        title: "Reopen alert",
        message: "Are you sure you want to reopen this alert?",
        confirmText: "Reopen",
        cancelText: "Cancel"
    }, 'static');
}

interface AlertStatusConfig {
    colorClass: string;
    icon: string;
}